import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, MessageCircle, Sun, Moon, Monitor } from 'lucide-react';
import { Button } from '../ui/button';
import { useTheme } from '@/context/ThemeContext';
import { useAssetContext } from '@/context/AssetContext';
import { fetchWithRetry } from '@/utils/fetchWithRetry';
import { useSections } from '@/hooks/useSections';
import CountryFlag from '@/components/CountryFlag';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "../ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "../ui/hover-card";

const API_URL = window.location.origin;

// Default navigation data (fallback if CMS unavailable)
const defaultDestinations = [
  { name: 'Australia', href: '/destinations/australia', flag: '🇦🇺' },
  { name: 'Canada', href: '/destinations/canada', flag: '🇨🇦' },
  { name: 'Dubai', href: '/destinations/dubai', flag: '🇦🇪' },
  { name: 'France', href: '/destinations/france', flag: '🇫🇷' },
  { name: 'Germany', href: '/destinations/germany', flag: '🇩🇪' },
  { name: 'Ireland', href: '/destinations/ireland', flag: '🇮🇪' },
  { name: 'Malaysia', href: '/destinations/malaysia', flag: '🇲🇾' },
  { name: 'New Zealand', href: '/destinations/new-zealand', flag: '🇳🇿' },
  { name: 'Singapore', href: '/destinations/singapore', flag: '🇸🇬' },
  { name: 'UK', href: '/destinations/uk', flag: '🇬🇧' },
  { name: 'USA', href: '/destinations/usa', flag: '🇺🇸' },
];

const defaultServices = [
  { name: 'Profile Assessment', href: '/services#profile-assessment' },
  { name: 'University Shortlisting', href: '/services#university-shortlisting' },
  { name: 'Application Support', href: '/services#application-support' },
  { name: 'SOP & LOR Writing', href: '/services#sop-lor' },
  { name: 'Visa Assistance', href: '/services#visa-assistance' },
];

const defaultStudentServices = [
  { name: 'Education Loan', href: '/student-services/education-loan', icon: '💰', description: 'Easy financing options for your education' },
  { name: 'Forex Services', href: '/student-services/forex', icon: '💱', description: 'Best rates for international transfers' },
  { name: 'Student Housing', href: '/student-services/housing', icon: '🏠', description: 'Safe & affordable accommodation' },
  { name: 'Health Insurance', href: '/student-services/insurance', icon: '🛡️', description: 'Comprehensive coverage abroad' },
];

const defaultPopularPrograms = [
  { name: 'MS in Computer Science - USA', href: '/programs/ms-in-computer-science-usa' },
  { name: 'MBA in Canada', href: '/programs/mba-in-canada' },
  { name: 'MS in Data Science - UK', href: '/programs/ms-in-data-science-uk' },
  { name: 'MBBS in Russia', href: '/programs/mbbs-in-russia' },
  { name: 'MBBS Abroad', href: '/programs/mbbs-abroad' },
  { name: 'Engineering in Germany', href: '/programs/engineering-in-germany' },
  { name: 'MS in AI - USA', href: '/programs/ms-in-artificial-intelligence-usa' },
];

const defaultPopularComparisons = [
  // Real detail-page slugs — all 4 verified GET /api/comparisons/public/{slug} → 200
  // with substantive comparison tables + detailed analysis sections.
  { name: 'MS in USA vs UK vs Canada', href: '/compare/ms-in-usa-vs-uk-vs-canada' },
  { name: 'MBA vs MS: Which is Better?', href: '/compare/mba-vs-ms-which-is-better' },
  { name: 'Canada vs Australia', href: '/compare/study-in-canada-vs-australia' },
  { name: 'USA vs Germany', href: '/compare/ms-usa-vs-masters-germany' },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const location = useLocation();
  const { theme, isDark, toggleTheme, resetToAuto, isManualOverride } = useTheme();
  const { getLogo } = useAssetContext();

  // Section-based CMS (Phase B Step 4). CTA label/href editable from admin.
  const { getSection: getHeaderSection } = useSections('header');
  const topCtaSec = getHeaderSection('top_cta')?.content || {};
  const ctaLabel = topCtaSec.button?.label || 'Book Consultation';
  const ctaHref = topCtaSec.button?.href || '/contact';
  const mobileCtaLabel = topCtaSec.mobile_button?.label || ctaLabel;
  const mobileCtaHref = topCtaSec.mobile_button?.href || ctaHref;

  // Batch 2: destinations mega-menu editable via admin/sections
  const destinationsMenuSec = getHeaderSection('destinations_menu')?.content;
  
  // Check if user has visited portal before
  useEffect(() => {
    // Check various indicators that user has visited/logged in before
    const hasVisited = localStorage.getItem('portal_visited') === 'true';
    const hasZohoVerified = Object.keys(localStorage).some(key => key.startsWith('zoho_verified_'));
    const hasStudentToken = document.cookie.includes('student_token');
    
    setIsReturningUser(hasVisited || hasZohoVerified || hasStudentToken);
  }, []);
  
  // CMS Navigation State
  const [navSettings, setNavSettings] = useState({
    destinations: defaultDestinations,
    services: defaultServices,
    student_services: defaultStudentServices,
    programs: defaultPopularPrograms,
    comparisons: defaultPopularComparisons
  });
  
  // Fetch navigation settings from CMS
  useEffect(() => {
    const fetchNavSettings = async () => {
      const res = await fetchWithRetry(`${API_URL}/api/cms/site-settings/navigation`, {}, { label: 'navigation' });
      if (res && res.ok) {
        const data = await res.json();
        setNavSettings(prev => ({ ...prev, ...data }));
      }
    };
    fetchNavSettings();
  }, []);
  
  // Get active links from CMS or defaults
  const legacyDestinations = (navSettings.destinations || defaultDestinations).filter(l => l.is_active !== false);
  const services = (navSettings.services || defaultServices).filter(l => l.is_active !== false);
  const studentServices = (navSettings.student_services || defaultStudentServices).filter(l => l.is_active !== false);
  const popularPrograms = (navSettings.programs || defaultPopularPrograms).filter(l => l.is_active !== false);
  const popularComparisons = (navSettings.comparisons || defaultPopularComparisons).filter(l => l.is_active !== false);

  // Phase B Step 4 Batch 2: if the `destinations_menu` section is published, flatten
  // its columns → single list matching the header's flat {name, href, flag} shape.
  // Flags are looked up from the legacy list by href (fallback: empty). Legacy list
  // stays the source of truth if the section is absent/empty.
  let destinations = legacyDestinations;
  if (destinationsMenuSec && Array.isArray(destinationsMenuSec.columns) && destinationsMenuSec.columns.length > 0) {
    const flagByHref = new Map(legacyDestinations.map(d => [d.href, d.flag]));
    const flat = [];
    for (const col of destinationsMenuSec.columns) {
      for (const l of (col.links || [])) {
        if (!l?.href || !l?.label) continue;
        flat.push({
          name: l.label.replace(/^Study in\s+/i, ''),
          href: l.href,
          flag: flagByHref.get(l.href) || '',
        });
      }
    }
    if (flat.length > 0) destinations = flat;
  }

  // Sort destinations alphabetically by display name so the mega-menu always
  // reads in a predictable order regardless of CMS entry order.
  destinations = [...destinations].sort((a, b) =>
    (a?.name || '').localeCompare(b?.name || '', 'en', { sensitivity: 'base' })
  );

  // Pages with hero images that need white logo when not scrolled
  const pagesWithHeroImage = ['/', '/about', '/services', '/events', '/blog', '/contact', '/success-stories'];
  const isDestinationPage = location.pathname.startsWith('/destinations');
  const hasHeroBackground = pagesWithHeroImage.includes(location.pathname) || isDestinationPage;
  
  // Logo should be white when: on hero page (dark-gradient header) in dark mode
  // OR always in dark mode. In light mode we now keep the header white on
  // hero pages too, so the logo stays coloured.
  const useWhiteLogo = (hasHeroBackground && !isScrolled && isDark) || isDark;
  // Text should be white/light only when the header itself is dark — i.e. dark
  // mode on a hero page. In light mode the header is white so text stays dark.
  const useHeroStyling = hasHeroBackground && !isScrolled && isDark;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? isDark 
            ? 'bg-[#1a2830]/95 backdrop-blur-md shadow-lg border-b border-white/10' 
            : 'bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100'
          : hasHeroBackground
            ? isDark
              ? 'bg-gradient-to-b from-[#1a2830]/90 via-[#1a2830]/60 to-transparent'
              : 'bg-white/90 backdrop-blur-md shadow-sm border-b border-gray-100'
            : isDark 
              ? 'bg-[#1a2830]/80 backdrop-blur-sm'
              : 'bg-white/80 backdrop-blur-sm'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={getLogo()} 
              alt="UniXperts" 
              className={`h-8 md:h-10 w-auto transition-all ${useWhiteLogo ? 'brightness-0 invert' : ''}`}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <Link to="/" className={`px-4 py-2 text-sm font-medium transition-colors ${useHeroStyling || isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    Home
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`text-sm font-medium bg-transparent ${useHeroStyling || isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    Destinations
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className={`grid w-[400px] gap-1 p-4 md:w-[500px] md:grid-cols-2 ${isDark ? 'bg-[#233842]' : 'bg-white'}`}>
                      {destinations.map((dest) => (
                        <li key={dest.name}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={dest.href}
                              className={`flex items-center gap-2 rounded-md p-3 transition-colors ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-ux-blue-light text-ux-charcoal'}`}
                            >
                              <CountryFlag country={dest.name} emoji={dest.flag} size="md" />
                              <span className="text-sm font-medium">{dest.name}</span>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                      <li className="col-span-2 mt-2 border-t pt-2">
                        <NavigationMenuLink asChild>
                          <Link
                            to="/destinations"
                            className={`flex items-center justify-center rounded-md p-3 transition-colors text-sm font-medium ${isDark ? 'bg-ux-cyan/20 hover:bg-ux-cyan hover:text-white text-ux-cyan' : 'bg-ux-blue-light hover:bg-ux-blue hover:text-white text-ux-blue'}`}
                          >
                            View All Destinations
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`text-sm font-medium bg-transparent ${useHeroStyling || isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className={`grid w-[500px] grid-cols-2 gap-2 p-4 ${isDark ? 'bg-[#233842]' : 'bg-white'}`}>
                      <div>
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 px-3 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Our Services</p>
                        <ul className="space-y-1">
                          {services.map((service) => (
                            <li key={service.name}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={service.href}
                                  className={`block rounded-md p-3 transition-colors text-sm font-medium ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-ux-blue-light text-ux-charcoal'}`}
                                >
                                  {service.name}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className={`text-xs font-semibold uppercase tracking-wider mb-2 px-3 ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>Student Tools</p>
                        <ul className="space-y-1">
                          {studentServices.slice(0, 4).map((service) => (
                            <li key={service.name}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={service.href}
                                  className={`flex items-center gap-2 rounded-md p-3 transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-ux-blue-light'}`}
                                >
                                  <span className="text-lg">{service.icon}</span>
                                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                                    {service.name}
                                  </span>
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className={`text-sm font-medium bg-transparent ${useHeroStyling || isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    Programs
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className={`grid w-[380px] gap-1 p-4 ${isDark ? 'bg-[#233842]' : 'bg-white'}`}>
                      {/* Featured: IB Pathway Explorer */}
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            to="/ib"
                            data-testid="nav-ib-pathway"
                            className={`block rounded-md p-3 transition-all border ${
                              isDark
                                ? 'bg-gradient-to-br from-ux-cyan/15 to-blue-500/10 border-ux-cyan/30 hover:border-ux-cyan text-white'
                                : 'bg-gradient-to-br from-ux-cyan/10 to-blue-50 border-ux-cyan/30 hover:border-ux-cyan text-ux-charcoal'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-sm leading-tight flex items-center gap-1.5">
                                  <span aria-hidden>🎓</span> IB Pathway Explorer
                                </p>
                                <p className={`text-[11px] mt-0.5 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                                  10 faculties · 614 universities · for IB students
                                </p>
                              </div>
                              <span className={`text-[9px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 flex-shrink-0 ${isDark ? 'bg-ux-cyan text-[#0f1c22]' : 'bg-ux-cyan text-white'}`}>
                                New
                              </span>
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      <li className={`my-1 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} aria-hidden />
                      {popularPrograms.map((program) => (
                        <li key={program.name}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={program.href}
                              className={`block rounded-md p-3 transition-colors text-sm font-medium ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-ux-blue-light text-ux-charcoal'}`}
                            >
                              {program.name}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                      <li className="mt-2 border-t pt-2">
                        <NavigationMenuLink asChild>
                          <Link
                            to="/programs"
                            className={`flex items-center justify-center rounded-md p-3 transition-colors text-sm font-medium ${isDark ? 'bg-ux-cyan/20 hover:bg-ux-cyan hover:text-white text-ux-cyan' : 'bg-ux-blue-light hover:bg-ux-blue hover:text-white text-ux-blue'}`}
                          >
                            View All Programs
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  {/*
                    Compare uses a Radix HoverCard instead of NavigationMenu.
                    Radix's shared NavigationMenuViewport is centred under the
                    entire nav row — every content renders in that single spot,
                    which visually misaligns for a narrow, right-side menu.
                    HoverCard anchors its content directly to the trigger and
                    accepts `align="end"` so a 320px panel pins to the right
                    edge of the "Compare" button — regardless of where in the
                    nav row the button sits.
                  */}
                  <HoverCard openDelay={100} closeDelay={150}>
                    <HoverCardTrigger asChild>
                      <button
                        type="button"
                        className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap bg-transparent ${useHeroStyling || isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}
                        data-testid="compare-nav-trigger"
                      >
                        Compare
                        <ChevronDown className="w-3 h-3" aria-hidden="true" />
                      </button>
                    </HoverCardTrigger>
                    <HoverCardContent
                      align="end"
                      sideOffset={8}
                      className={`w-[320px] p-4 ${isDark ? 'bg-[#233842] border-white/10' : 'bg-white'}`}
                      data-testid="compare-mega-menu"
                    >
                      <ul className="grid gap-1">
                        {popularComparisons.map((comp) => (
                          <li key={comp.name}>
                            <Link
                              to={comp.href}
                              className={`block rounded-md p-3 transition-colors text-sm font-medium ${isDark ? 'hover:bg-white/10 text-white' : 'hover:bg-purple-50 text-ux-charcoal'}`}
                            >
                              {comp.name}
                            </Link>
                          </li>
                        ))}
                        <li className="mt-2 border-t pt-2">
                          <Link
                            to="/compare"
                            className={`flex items-center justify-center rounded-md p-3 transition-colors text-sm font-medium ${isDark ? 'bg-purple-500/20 hover:bg-purple-500 hover:text-white text-purple-400' : 'bg-purple-50 hover:bg-purple-600 hover:text-white text-purple-700'}`}
                          >
                            View All Comparisons
                          </Link>
                        </li>
                      </ul>
                    </HoverCardContent>
                  </HoverCard>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link to="/about" className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${useHeroStyling || isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    About
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link to="/events" className={`px-3 py-2 text-sm font-medium transition-colors ${useHeroStyling || isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    Events
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link to="/blog" className={`px-3 py-2 text-sm font-medium transition-colors ${useHeroStyling || isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    Blog
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link to="/contact" className={`px-3 py-2 text-sm font-medium transition-colors ${useHeroStyling || isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    Contact
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Theme Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className={`ml-2 ${useHeroStyling || isDark ? 'text-white hover:bg-white/10' : 'text-ux-charcoal hover:bg-ux-charcoal/10'}`}
                  data-testid="theme-toggle"
                >
                  {isDark ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={isDark ? 'bg-[#233842] border-white/10' : ''}>
                <DropdownMenuItem onClick={() => toggleTheme()} className={isDark ? 'text-white hover:bg-white/10' : ''}>
                  {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                  {isDark ? 'Light Mode' : 'Dark Mode'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={resetToAuto} className={isDark ? 'text-white hover:bg-white/10' : ''}>
                  <Monitor className="mr-2 h-4 w-4" />
                  Auto (Time-based)
                  {!isManualOverride && <span className="ml-2 text-xs text-ux-cyan">✓</span>}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="flex items-center gap-2 ml-2">
              <Button 
                size="sm"
                className="bg-gradient-to-r from-ux-cyan to-ux-violet text-white hover:opacity-90 font-semibold"
                asChild
              >
                <Link to={ctaHref} data-testid="book-consultation-btn">{ctaLabel}</Link>
              </Button>
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button 
                variant="ghost" 
                size="icon" 
                className={`${useHeroStyling || isDark ? 'text-white hover:bg-white/20' : 'text-ux-charcoal hover:bg-ux-charcoal/10'}`}
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" isDark={isDark} className="w-[300px] sm:w-[350px] overflow-y-auto">
              <nav className="flex flex-col gap-2 mt-6 pb-6">
                <Link to="/" className={`text-base font-medium py-1.5 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                  Home
                </Link>
                
                <div className="space-y-1">
                  <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Destinations</p>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                    {destinations.map((dest) => (
                      <Link 
                        key={dest.name}
                        to={dest.href} 
                        className={`flex items-center gap-1.5 text-sm py-0.5 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}
                      >
                        <CountryFlag country={dest.name} emoji={dest.flag} size="sm" />
                        <span>{dest.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                <Link to="/services" className={`text-base font-medium py-1.5 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                  Services
                </Link>
                
                <div className="space-y-1">
                  <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Student Essentials</p>
                  <div className="space-y-0.5">
                    {studentServices.map((service) => (
                      <Link 
                        key={service.name}
                        to={service.href} 
                        className={`flex items-center gap-2 text-sm py-1 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}
                      >
                        <span>{service.icon}</span>
                        <span>{service.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Programs Section for Mobile */}
                <div className="space-y-1">
                  <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Popular Programs</p>
                  <div className="space-y-0.5">
                    <Link
                      to="/ib"
                      data-testid="nav-ib-pathway-mobile"
                      className={`block rounded-md p-2.5 mb-1.5 border ${
                        isDark
                          ? 'bg-ux-cyan/10 border-ux-cyan/30 text-white'
                          : 'bg-ux-cyan/5 border-ux-cyan/30 text-ux-charcoal'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <p className="font-bold text-sm flex items-center gap-1.5">
                        <span aria-hidden>🎓</span> IB Pathway Explorer
                        <span className={`ml-auto text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 ${isDark ? 'bg-ux-cyan text-[#0f1c22]' : 'bg-ux-cyan text-white'}`}>New</span>
                      </p>
                      <p className={`text-[11px] mt-0.5 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>For IB students · 614 universities</p>
                    </Link>
                    {popularPrograms.slice(0, 5).map((program) => (
                      <Link 
                        key={program.name}
                        to={program.href} 
                        className={`block text-sm py-1 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {program.name}
                      </Link>
                    ))}
                    <Link 
                      to="/programs" 
                      className={`block text-sm py-1 font-medium ${isDark ? 'text-ux-cyan hover:text-ux-cyan/80' : 'text-ux-blue hover:text-ux-blue/80'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      View All Programs →
                    </Link>
                  </div>
                </div>

                {/* Compare Section for Mobile */}
                <div className="space-y-1">
                  <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Compare Options</p>
                  <div className="space-y-0.5">
                    {popularComparisons.map((comp) => (
                      <Link 
                        key={comp.name}
                        to={comp.href} 
                        className={`block text-sm py-1 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {comp.name}
                      </Link>
                    ))}
                    <Link 
                      to="/compare" 
                      className={`block text-sm py-1 font-medium ${isDark ? 'text-ux-cyan hover:text-ux-cyan/80' : 'text-ux-blue hover:text-ux-blue/80'}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      View All Comparisons →
                    </Link>
                  </div>
                </div>
                
                <Link to="/about" className={`text-base font-medium py-1.5 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                  About Us
                </Link>
                <Link to="/events" className={`text-base font-medium py-1.5 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                  Events
                </Link>
                <Link to="/blog" className={`text-base font-medium py-1.5 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                  Blog
                </Link>
                <Link to="/locations" className={`text-base font-medium py-1.5 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                  Locations
                </Link>
                <Link to="/contact" className={`text-base font-medium py-1.5 ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                  Contact
                </Link>

                <div className={`flex flex-col gap-3 mt-3 pt-3 border-t ${isDark ? 'border-white/10' : ''}`}>
                  {/* Theme Toggle for Mobile */}
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                      {isDark ? 'Dark Mode' : 'Light Mode'}
                    </span>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={toggleTheme}
                      className={`${isDark ? 'text-white hover:bg-white/10' : 'text-ux-charcoal hover:bg-ux-charcoal/10'}`}
                    >
                      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                    </Button>
                  </div>
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-ux-cyan to-ux-violet text-white hover:opacity-90 font-semibold"
                    asChild
                  >
                    <Link to={mobileCtaHref} data-testid="mobile-book-consultation-btn">
                      {mobileCtaLabel}
                    </Link>
                  </Button>
                </div>

                <div className={`flex items-center gap-4 mt-3 pt-3 border-t ${isDark ? 'border-white/10' : ''}`}>
                  <a href="tel:+919639557799" className={`flex items-center gap-2 text-sm ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    <Phone className="h-4 w-4" />
                    <span>Call Us</span>
                  </a>
                  <a href="https://wa.me/919639557799" className={`flex items-center gap-2 text-sm ${isDark ? 'text-white hover:text-ux-cyan' : 'text-ux-charcoal hover:text-ux-blue'}`}>
                    <MessageCircle className="h-4 w-4" />
                    <span>WhatsApp</span>
                  </a>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
